import { useCallback, useReducer, useRef } from 'react';
import { streamChat } from './api';

// FIN-1314: the state of a conversation. Two views of it: `messages` is what the panel
// shows (text as it streams, the traces of the tools), `transcript` is what the model saw —
// the backend sends it back after every turn and takes it with the next message. The server
// records every turn under the conversation's id, so an old conversation can be loaded back
// from the chats page and continued (`load`).

let nextId = 1;
const id = () => {
  nextId += 1;
  return nextId;
};

// The id of a conversation goes with every message, so the turns the backend records can be
// read back as one conversation; a new conversation gets a new one
const conversationId = () => (
  window.crypto && window.crypto.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`
);

export const initialState = { messages: [], transcript: [], status: 'idle', error: null, conversationId: null, title: null };

const fresh = () => ({ ...initialState, conversationId: conversationId() });

const last = (messages) => messages[messages.length - 1];

// The server names a conversation after its first question, cut the same way
const titleOf = (question) => {
  const text = question.replace(/\s+/g, ' ').trim();
  return text.length > 80 ? `${text.slice(0, 77)}...` : text;
};

const updateLast = (messages, change) => [...messages.slice(0, -1), { ...last(messages), ...change }];

// A recorded conversation as the panel shows it: the question, then the answer with its traces
export const messagesOf = (turns) => turns.flatMap((turn) => [
  { id: id(), role: 'user', text: turn.question || '' },
  { id: id(), role: 'assistant', text: turn.answer || (turn.error ? `_${turn.error}_` : ''), traces: turn.tools || [] },
]);

export const reducer = (state, action) => {
  switch (action.type) {
    case 'send':
      return {
        ...state,
        status: 'busy',
        error: null,
        title: state.messages.length === 0 && !state.title ? titleOf(action.text) : state.title,
        messages: [
          ...state.messages,
          { id: id(), role: 'user', text: action.text },
          { id: id(), role: 'assistant', text: '', traces: [] },
        ],
      };
    case 'text':
      return { ...state, messages: updateLast(state.messages, { text: last(state.messages).text + action.delta }) };
    case 'tool': {
      const traces = last(state.messages).traces;
      const known = traces.findIndex((trace) => trace.id === action.trace.id);
      const next = known === -1
        ? [...traces, action.trace]
        : traces.map((trace, index) => (index === known ? { ...trace, ...action.trace } : trace));
      return { ...state, messages: updateLast(state.messages, { traces: next }) };
    }
    case 'transcript':
      return { ...state, transcript: [...state.transcript, ...action.transcript] };
    case 'done':
      return { ...state, status: 'idle' };
    case 'error':
      return { ...state, status: 'idle', error: action.message };
    case 'load':
      return {
        ...fresh(),
        conversationId: action.conversation.id,
        title: action.conversation.title,
        messages: messagesOf(action.turns || []),
        transcript: action.transcript || [],
      };
    case 'title':
      return { ...state, title: action.title };
    case 'reset':
      return fresh();
    default:
      return state;
  }
};

// `onTurn(conversationId)` is told when a turn ends, well or not — the chats page refreshes
// its list and puts the conversation into the address bar
export const useAssistant = (onTurn) => {
  const [state, dispatch] = useReducer(reducer, undefined, fresh);
  const controller = useRef(null);
  // The number of the current turn: a turn cut off by New conversation must not write its late
  // events, its "Stopped." or its end into the conversation that replaced it
  const turn = useRef(0);
  const transcript = useRef(state.transcript);
  transcript.current = state.transcript;
  const conversation = useRef(state.conversationId);
  conversation.current = state.conversationId;
  const ended = useRef(onTurn);
  ended.current = onTurn;

  const send = useCallback(async (text, page) => {
    const question = text.trim();
    if (!question) return;

    turn.current += 1;
    const mine = turn.current;
    const live = () => turn.current === mine;
    const current = conversation.current;
    dispatch({ type: 'send', text: question });
    const abort = new AbortController();
    controller.current = abort;
    const onEvent = ({ event, data }) => {
      if (!live()) return;
      if (event === 'text') dispatch({ type: 'text', delta: String(data) });
      else if (event === 'tool') dispatch({ type: 'tool', trace: data });
      else if (event === 'transcript') dispatch({ type: 'transcript', transcript: data });
      else if (event === 'done') dispatch({ type: 'done' });
      else if (event === 'error') dispatch({ type: 'error', message: (data && data.message) || String(data) });
    };
    try {
      await streamChat(
        { text: question, page, transcript: transcript.current, conversation_id: current },
        onEvent, abort.signal,
      );
      if (live()) dispatch({ type: 'done' });
    } catch (error) {
      if (live()) dispatch({ type: 'error', message: error.name === 'AbortError' ? 'Stopped.' : error.message });
    } finally {
      if (controller.current === abort) controller.current = null;
      if (live() && ended.current) ended.current(current);
    }
  }, []);

  const stop = useCallback(() => {
    if (controller.current) controller.current.abort();
  }, []);

  const reset = useCallback(() => {
    stop();
    turn.current += 1;
    dispatch({ type: 'reset' });
  }, [stop]);

  // A recorded conversation, as the chats page fetched it: { conversation, turns, transcript }
  const load = useCallback((recorded) => {
    stop();
    turn.current += 1;
    dispatch({ type: 'load', ...recorded });
  }, [stop]);

  const rename = useCallback((title) => dispatch({ type: 'title', title }), []);

  return { ...state, send, stop, reset, load, rename };
};
